import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ClipboardList, Plus, Search, Building, User, Calendar, FileText, Trash2, Box } from 'lucide-react';
import { toast } from 'sonner';

const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let BACKEND_URL = !ENV_BACKEND_URL || ENV_BACKEND_URL === '' ? window.location.origin : ENV_BACKEND_URL;
if (BACKEND_URL.endsWith('/api')) BACKEND_URL = BACKEND_URL.slice(0, -4);
const API = `${BACKEND_URL}/api`;

export default function SolicitudesMaterialesPage() {
  const [requisitions, setRequisitions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReq, setSelectedReq] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // New Requisition State
  const [newReq, setNewReq] = useState({
      project_id: '',
      cost_center_id: '',
      notes: '',
      items: [{ product_id: '', product_name: '', quantity: 1 }]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reqsRes, projRes, prodRes] = await Promise.all([
        axios.get(`${API}/construction/requisitions`),
        axios.get(`${API}/construction/projects`),
        axios.get(`${API}/products`)
      ]);
      setRequisitions(reqsRes.data);
      setProjects(projRes.data);
      setProducts(prodRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // WebSocket Integration
  const { socket } = require('../contexts/AuthContext').useAuth();
  useEffect(() => {
    if (!socket) return;

    const handleMessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'CONSTRUCTION_UPDATE' || message.type === 'INVENTORY_UPDATE') {
          console.log('Solicitudes real-time update:', message.type);
          fetchData();
        }
      } catch (e) {
        console.error('Error parsing WS message:', e);
      }
    };

    socket.addEventListener('message', handleMessage);
    return () => socket.removeEventListener('message', handleMessage);
  }, [socket]);

  const handleProjectChange = async (projectId) => {
      setNewReq({ ...newReq, project_id: projectId, cost_center_id: '' });
      try {
          const res = await axios.get(`${API}/construction/projects/${projectId}/cost-centers`);
          setCostCenters(res.data);
      } catch (error) {
          toast.error('Error al cargar centros de costo');
      }
  };

  const addItem = () => {
      setNewReq({ ...newReq, items: [...newReq.items, { product_id: '', product_name: '', quantity: 1 }] });
  };

  const removeItem = (index) => {
      const updatedItems = newReq.items.filter((_, i) => i !== index);
      setNewReq({ ...newReq, items: updatedItems });
  };

  const updateItem = (index, field, value) => {
      const updatedItems = [...newReq.items];
      if (field === 'product_id') {
          const prod = products.find(p => p.id === value);
          updatedItems[index].product_id = value;
          updatedItems[index].product_name = prod ? prod.name : '';
      } else {
          updatedItems[index][field] = value;
      }
      setNewReq({ ...newReq, items: updatedItems });
  };

  const handleCreate = async (e) => {
      e.preventDefault();
      if (!newReq.project_id || !newReq.cost_center_id) {
          toast.error('Seleccione obra y centro de costo');
          return;
      }
      if (newReq.items.some(i => !i.product_id || i.quantity <= 0)) {
          toast.error('Seleccione productos válidos y cantidades');
          return;
      }

      try {
          await axios.post(`${API}/construction/requisitions`, newReq);
          toast.success('Solicitud enviada correctamente');
          setIsCreateOpen(false);
          setNewReq({ project_id: '', cost_center_id: '', notes: '', items: [{ product_id: '', product_name: '', quantity: 1 }] });
          fetchData();
      } catch (error) {
          toast.error('Error al enviar la solicitud');
      }
  };

  const filteredReqs = requisitions.filter(r => 
    r.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (r.notes && r.notes.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ClipboardList className="h-8 w-8 text-indigo-600" />
            Solicitudes de Materiales
          </h1>
          <p className="text-muted-foreground">Gestión de suministros por obra conectada al inventario POS</p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="w-full md:w-auto"><Plus className="mr-2 h-4 w-4" /> Nueva Solicitud</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nueva Solicitud de Materiales</DialogTitle>
              <DialogDescription>Seleccione productos del inventario oficial del sistema</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-6 py-4">
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Obra</label>
                        <Select onValueChange={handleProjectChange}>
                            <SelectTrigger>
                                <SelectValue placeholder="Seleccione obra" />
                            </SelectTrigger>
                            <SelectContent>
                                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Capítulo / Partida</label>
                        <Select 
                          disabled={!newReq.project_id} 
                          onValueChange={(val) => setNewReq({...newReq, cost_center_id: val})}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Seleccione partida" />
                            </SelectTrigger>
                            <SelectContent>
                                {costCenters.map(cc => <SelectItem key={cc.id} value={cc.id}>{cc.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h4 className="text-sm font-bold flex items-center gap-2">
                            <Box className="h-4 w-4 text-indigo-500" /> Materiales del Inventario
                        </h4>
                        <Button type="button" variant="outline" size="sm" onClick={addItem}>
                            <Plus className="h-4 w-4 mr-1" /> Agregar Ítem
                        </Button>
                    </div>
                    
                    <div className="space-y-3">
                        {newReq.items.map((item, idx) => (
                            <div key={idx} className="flex gap-3 items-end bg-muted/20 p-3 rounded-lg border">
                                <div className="flex-1 space-y-1">
                                    <label className="text-[10px] uppercase font-bold text-muted-foreground">Producto</label>
                                    <Select 
                                      value={item.product_id} 
                                      onValueChange={(val) => updateItem(idx, 'product_id', val)}
                                    >
                                        <SelectTrigger className="bg-white">
                                            <SelectValue placeholder="Buscar en inventario..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {products.map(p => (
                                                <SelectItem key={p.id} value={p.id}>{p.name} - Stock: {p.stock}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="w-24 space-y-1">
                                    <label className="text-[10px] uppercase font-bold text-muted-foreground">Cant.</label>
                                    <Input 
                                      type="number" 
                                      className="bg-white"
                                      value={item.quantity} 
                                      onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value))}
                                    />
                                </div>
                                {newReq.items.length > 1 && (
                                    <Button type="button" variant="ghost" size="icon" className="text-red-500" onClick={() => removeItem(idx)}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-medium">Notas / Observaciones</label>
                    <Input 
                      placeholder="Ej: Entrega urgente antes de las 10am" 
                      value={newReq.notes} 
                      onChange={(e) => setNewReq({...newReq, notes: e.target.value})}
                    />
                </div>

                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">Enviar Solicitud</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input 
          className="pl-10" 
          placeholder="Buscar por ID o notas..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="border rounded-xl bg-white dark:bg-card overflow-hidden shadow-sm">
          <table className="w-full text-left text-sm">
              <thead className="bg-muted/50 border-b">
                  <tr>
                      <th className="px-6 py-4 font-bold uppercase tracking-wider text-[10px] text-muted-foreground">Folio / Fecha</th>
                      <th className="px-6 py-4 font-bold uppercase tracking-wider text-[10px] text-muted-foreground">Obra / Destino</th>
                      <th className="px-6 py-4 font-bold uppercase tracking-wider text-[10px] text-muted-foreground">Ítems</th>
                      <th className="px-6 py-4 font-bold uppercase tracking-wider text-[10px] text-muted-foreground">Estado</th>
                      <th className="px-6 py-4 text-right"></th>
                  </tr>
              </thead>
              <tbody className="divide-y">
                  {filteredReqs.map((req) => (
                    <tr key={req.id} className="hover:bg-muted/5 transition-colors">
                        <td className="px-6 py-4">
                            <p className="font-bold text-indigo-600">SM-{req.id.split('-').pop()}</p>
                            <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                                <Calendar className="h-3 w-3" /> {new Date(req.created_at).toLocaleDateString()}
                            </p>
                        </td>
                        <td className="px-6 py-4">
                            <div className="flex items-center gap-2 font-medium">
                                <Building className="h-3 w-3 text-muted-foreground" /> 
                                {projects.find(p => p.id === req.project_id)?.name || req.project_id}
                            </div>
                            <p className="text-[11px] text-muted-foreground ml-5">{req.cost_center_id}</p>
                        </td>
                        <td className="px-6 py-4">
                            <Badge variant="secondary" className="font-mono text-[10px]">
                                {req.items?.length || 0} ítems
                            </Badge>
                        </td>
                        <td className="px-6 py-4">
                            <Badge className={`${
                                req.status === 'pending' ? 'bg-orange-100 text-orange-700 hover:bg-orange-100' :
                                req.status === 'approved' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' :
                                'bg-gray-100 text-gray-700'
                            } border-none font-bold text-[10px] uppercase`}>
                                {req.status === 'pending' ? 'Pendiente' : req.status}
                            </Badge>
                        </td>
                        <td className="px-6 py-4 text-right">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                              onClick={() => {
                                setSelectedReq(req);
                                setIsDetailsOpen(true);
                              }}
                            >
                                Ver Detalle
                            </Button>
                        </td>
                    </tr>
                  ))}
                  {filteredReqs.length === 0 && !loading && (
                    <tr>
                        <td colSpan={5} className="p-12 text-center text-muted-foreground">
                            <div className="flex flex-col items-center gap-2 opacity-50">
                                <ClipboardList className="h-12 w-12" />
                                <p>No se encontraron solicitudes de materiales.</p>
                            </div>
                        </td>
                    </tr>
                  )}
              </tbody>
          </table>
      </div>

      {/* Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <DialogContent className="max-w-2xl">
              <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                     <FileText className="h-5 w-5 text-indigo-600" />
                     Detalle de Solicitud SM-{selectedReq?.id.split('-').pop()}
                  </DialogTitle>
                  <DialogDescription>
                      Información de insumos solicitados para la faena
                  </DialogDescription>
              </DialogHeader>
              
              {selectedReq && (
                  <div className="space-y-6 py-4">
                      <div className="grid grid-cols-2 gap-6 bg-muted/30 p-4 rounded-xl">
                          <div className="space-y-1">
                              <span className="text-[10px] font-bold text-muted-foreground uppercase">Obra</span>
                              <p className="font-semibold text-sm">{projects.find(p => p.id === selectedReq.project_id)?.name || selectedReq.project_id}</p>
                          </div>
                          <div className="space-y-1">
                              <span className="text-[10px] font-bold text-muted-foreground uppercase">Capítulo</span>
                              <p className="font-semibold text-sm">{selectedReq.cost_center_id}</p>
                          </div>
                          <div className="space-y-1">
                              <span className="text-[10px] font-bold text-muted-foreground uppercase">Fecha Solicitud</span>
                              <p className="font-semibold text-sm">{new Date(selectedReq.created_at).toLocaleString()}</p>
                          </div>
                          <div className="space-y-1">
                              <span className="text-[10px] font-bold text-muted-foreground uppercase">Estado Actual</span>
                              <Badge className="bg-orange-100 text-orange-700 border-none block w-fit h-fit py-0 px-2 text-[10px] uppercase font-bold">
                                {selectedReq.status}
                              </Badge>
                          </div>
                      </div>

                      <div className="space-y-3">
                          <h4 className="text-sm font-bold flex items-center gap-2">
                              Ítems Solicitados (Inventario)
                          </h4>
                          <div className="border rounded-xl overflow-hidden shadow-sm">
                              <table className="w-full text-sm">
                                  <thead className="bg-muted/50 border-b font-medium text-muted-foreground">
                                      <tr>
                                          <th className="px-4 py-2 text-left">Material / Producto</th>
                                          <th className="px-4 py-2 text-right">Cantidad</th>
                                          <th className="px-4 py-2 text-right">Stock Actual</th>
                                      </tr>
                                  </thead>
                                  <tbody className="divide-y">
                                      {selectedReq.items.map((item, idx) => {
                                          const prod = products.find(p => p.id === item.product_id);
                                          return (
                                              <tr key={idx}>
                                                  <td className="px-4 py-2 font-medium">{item.product_name}</td>
                                                  <td className="px-4 py-2 text-right font-mono font-bold text-indigo-600">{item.quantity}</td>
                                                  <td className="px-4 py-2 text-right text-muted-foreground text-xs">{prod ? prod.stock : 'N/A'}</td>
                                              </tr>
                                          );
                                      })}
                                  </tbody>
                              </table>
                          </div>
                      </div>

                      {selectedReq.notes && (
                          <div className="space-y-1">
                              <span className="text-[10px] font-bold text-muted-foreground uppercase">Instrucciones / Observaciones</span>
                              <p className="text-sm p-3 bg-muted/20 rounded-lg italic text-muted-foreground">"{selectedReq.notes}"</p>
                          </div>
                      )}

                      <div className="flex justify-end gap-3 pt-4 border-t">
                          <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>Cerrar</Button>
                          <Button className="bg-emerald-600 hover:bg-emerald-700">Aprobar y Despachar</Button>
                      </div>
                  </div>
              )}
          </DialogContent>
      </Dialog>
    </div>
  );
}
